<script lang="ts" setup>
import { defineProps, computed } from 'vue';

import Gauge from '@/components/shared/base/Gauge.vue';
import { MIC_MIN_RADIUS, MIC_MAX_RADIUS } from '@/utils/constants/microphone';

const volumeRatio = defineModel<number>('volumeRatio', {
  required: true,
  default: 5,
});

const props = withDefaults(
  defineProps<{
    effect?: number;
    minLabel?: string;
    maxLabel?: string;
    disabled?: boolean;

    min?: number;
    max?: number;
    increment?: number;
  }>(),
  {
    effect: 0,
    minLabel: 'Min',
    maxLabel: 'Max',
    disabled: false,

    min: 1,
    max: 10,
    increment: 1,
  }
);

const width = computed<number>(() => {
  if (props.effect < MIC_MIN_RADIUS) {
    return 0;
  }
  const range = MIC_MAX_RADIUS - MIC_MIN_RADIUS;
  const normalizedValue = (props.effect - MIC_MIN_RADIUS) / range;

  const scaledValue = Math.round(normalizedValue * 100 + 1);
  if (scaledValue < 1) {
    return 1;
  } else if (scaledValue > 100) {
    return 100;
  }
  return scaledValue;
});
</script>

<template>
  <div class="mic-control-container">
    <Gauge
      v-model="volumeRatio"
      :disabled="disabled"
      :min-label="minLabel"
      :max-label="maxLabel"
      :min="min"
      :max="max"
      :increment="increment"
    >
      <div
        v-if="effect"
        :style="{ width: `calc(${width}% + 0.5rem)` }"
        :class="{ 'background-effect': true, error: width >= 100 }"
      />
    </Gauge>
  </div>
</template>

<style scoped lang="scss">
.mic-control-container {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: fit-content;
  gap: 0.75rem;
  padding-top: 0.5rem;
}

.background-effect {
  position: absolute;
  left: -0.25rem;
  top: 50%;
  transform: translateY(-50%);
  height: calc(100% + 0.25rem);
  background-color: var(--surface-primary);
  opacity: 0.2;
  border-radius: 10px;
  padding-inline: 0.5rem;
  box-sizing: border-box;

  &.error {
    background-color: var(--error);
    opacity: 0.2;
  }
}
</style>
